/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useMutation, type MutationHookOptions } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  DedicatedEndpointCancelUpdateDocument,
  DedicatedEndpointCancelUpdateErrorKind,
  type DedicatedEndpointCancelUpdateMutation,
  type DedicatedEndpointCancelUpdateMutationVariables,
} from '@fai/friendli-suite/generated/client/graphql';
import { successProps, errorProps } from '@fai/ui/components/notification/props';

export const useEndpointCancelUpdate = (
  mutationOptions?: MutationHookOptions<
    DedicatedEndpointCancelUpdateMutation,
    DedicatedEndpointCancelUpdateMutationVariables
  >
) => {
  const { t } = useTranslation();
  const { teamId = '', projectId = '', endpointId = '' } = useParams();

  const [endpointCancelUpdateMutation, { loading }] = useMutation(
    DedicatedEndpointCancelUpdateDocument,
    {
      ...mutationOptions,
      onCompleted: (response) => {
        switch (response.dedicatedEndpointCancelUpdate.__typename) {
          case 'DedicatedEndpointCancelUpdateSuccess': {
            notifications.show({
              ...successProps,
              message: t('dedicated_endpoints.project.update_canceled'),
            });

            break;
          }

          case 'UserPermissionError':
          case 'TeamNotExistError': {
            notifications.show({
              ...errorProps,
              message: response.dedicatedEndpointCancelUpdate.message,
            });

            break;
          }

          case 'DedicatedEndpointNoUpdateToCancelError':
          case 'DedicatedEndpointVersionProcessedTooFarToCancelError': {
            switch (response.dedicatedEndpointCancelUpdate.errorKind) {
              case DedicatedEndpointCancelUpdateErrorKind.NoUpdateToCancel: {
                notifications.show({
                  ...errorProps,
                  message: t(
                    'dedicated_endpoints.project.endpoint_cancel_update_no_update_to_cancel_error'
                  ),
                });

                break;
              }

              case DedicatedEndpointCancelUpdateErrorKind.UpdateProcessedBeyondCancellation: {
                notifications.show({
                  ...errorProps,
                  message: t(
                    'dedicated_endpoints.project.endpoint_cancel_update_processed_beyond_cancellation_error'
                  ),
                });

                break;
              }

              default: {
                notifications.show({
                  ...errorProps,
                  message: response.dedicatedEndpointCancelUpdate.message,
                });

                break;
              }
            }

            break;
          }

          default: {
            break;
          }
        }

        if (mutationOptions?.onCompleted) {
          mutationOptions.onCompleted(response);
        }
      },
    }
  );

  const endpointCancelUpdate = () => {
    return endpointCancelUpdateMutation({
      variables: { input: { teamId, projectId, endpointId } },
    });
  };

  return { endpointCancelUpdate, loading };
};
